// File: frontend/src/pages/dashboards/AccountantDashboard.jsx

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/axios';

const AccountantDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [feeStats, setFeeStats] = useState(null);
  const [defaultersCount, setDefaultersCount] = useState(0);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAccountantData = async () => {
      try {
        setLoading(true);
        const [dashRes, defRes] = await Promise.all([
          api.get('/api/v1/fees/dashboard').catch(() => ({ data: null })),
          api.get('/api/v1/fees/defaulters').catch(() => ({ data: null }))
        ]);

        if (dashRes.data?.success) {
          setFeeStats(dashRes.data.data);
          setRecentTransactions(dashRes.data.data?.recent_transactions || []);
        }

        if (defRes.data?.success) {
          const list = defRes.data.data || [];
          setDefaultersCount(list.length);
        }
      } catch (error) {
        console.error('Error loading accountant dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccountantData();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error in AccountantDashboard:', error);
    }
  };

  const accountantName = user?.name || user?.email || 'Accountant';
  const totalCollected = feeStats?.total_collected !== undefined && feeStats?.total_collected !== null
    ? feeStats.total_collected
    : (feeStats?.collectedThisMonth || 0);

  const pendingBalance = feeStats?.total_pending !== undefined && feeStats?.total_pending !== null
    ? feeStats.total_pending
    : (feeStats?.pendingAmount || 0);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Header Navbar */}
      <header className="bg-slate-900 text-white border-b border-slate-800 sticky top-0 z-10 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-extrabold text-lg shadow-inner">
              💳
            </div>
            <div>
              <h1 className="font-bold text-white text-lg leading-tight">Accountant Financial Portal</h1>
              <p className="text-xs text-slate-400">Fees Collection, Receipts & Ledger</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col text-right">
              <span className="text-sm font-semibold text-slate-200">{accountantName}</span>
              <span className="text-xs text-emerald-400 font-bold tracking-wider uppercase">ACCOUNTANT</span>
            </div>

            <Link
              to="/profile"
              className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold px-3 py-2 rounded-xl text-xs transition border border-slate-700 flex items-center gap-1.5"
            >
              <span>👤</span> My Profile
            </Link>

            <button
              onClick={handleLogout}
              className="bg-slate-800 hover:bg-red-900/80 hover:text-white text-slate-300 font-medium px-4 py-2 rounded-xl text-xs transition border border-slate-700"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Welcome Financial Banner */}
        <div className="bg-gradient-to-r from-emerald-950 via-slate-900 to-emerald-900 rounded-2xl p-6 sm:p-8 text-white shadow-xl border border-emerald-900/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div className="space-y-2">
              <span className="inline-block px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-extrabold uppercase tracking-wider border border-emerald-500/30">
                ACCOUNTANT DASHBOARD
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold">
                Welcome back, {accountantName}!
              </h2>
              <p className="text-emerald-100 text-sm max-w-xl">
                Manage student fee collections, generate official receipts, monitor outstanding defaulters, and view real-time financial audit reports.
              </p>
            </div>
            <button
              onClick={() => navigate('/fees/collect')}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-6 py-3 rounded-xl shadow-lg transition flex items-center justify-center gap-2 text-sm shrink-0"
            >
              <span>💳</span> Collect Student Fee
            </button>
          </div>
        </div>

        {/* Fee Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-xl">
                💰
              </div>
              <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">Collected</span>
            </div>
            <p className="text-sm font-medium text-slate-500">Total Fees Collected</p>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-800 mt-1">
              {loading ? '...' : `Rs. ${Number(totalCollected).toLocaleString(undefined, { minimumFractionDigits: 0 })}`}
            </h3>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center font-bold text-xl">
                ⚠️
              </div>
              <span className="text-xs font-semibold text-red-600 bg-red-50 px-2.5 py-1 rounded-full">Pending</span>
            </div>
            <p className="text-sm font-medium text-slate-500">Pending Balance Dues</p>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-red-600 mt-1">
              {loading ? '...' : `Rs. ${Number(pendingBalance).toLocaleString(undefined, { minimumFractionDigits: 0 })}`}
            </h3>
          </div>

          <div 
            onClick={() => navigate('/fees/defaulters')}
            className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md cursor-pointer transition"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-xl">
                📱
              </div>
              <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">Defaulters</span>
            </div>
            <p className="text-sm font-medium text-slate-500">Defaulters Count</p>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-800 mt-1">
              {loading ? '...' : `${defaultersCount ?? 0} Students`}
            </h3>
          </div>

        </div>

        {/* Accountant Quick Actions */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <h3 className="font-bold text-slate-800 text-base">Quick Financial Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            
            <Link
              to="/fees/collect"
              className="p-5 rounded-xl bg-slate-50 hover:bg-emerald-50 hover:border-emerald-300 border border-slate-200 text-center transition group flex flex-col items-center justify-center"
            >
              <div className="text-3xl mb-2">💳</div>
              <span className="font-bold text-slate-800 group-hover:text-emerald-700 text-sm block">Collect Fee</span>
              <span className="text-[11px] text-slate-500 block mt-0.5">/fees/collect</span>
            </Link>

            <Link
              to="/reports/fees"
              className="p-5 rounded-xl bg-slate-50 hover:bg-blue-50 hover:border-blue-300 border border-slate-200 text-center transition group flex flex-col items-center justify-center"
            >
              <div className="text-3xl mb-2">📊</div>
              <span className="font-bold text-slate-800 group-hover:text-blue-700 text-sm block">Fee Report</span>
              <span className="text-[11px] text-slate-500 block mt-0.5">/reports/fees</span>
            </Link>

            <Link
              to="/fees/defaulters"
              className="p-5 rounded-xl bg-slate-50 hover:bg-amber-50 hover:border-amber-300 border border-slate-200 text-center transition group flex flex-col items-center justify-center"
            >
              <div className="text-3xl mb-2">📱</div>
              <span className="font-bold text-slate-800 group-hover:text-amber-700 text-sm block">Defaulters</span>
              <span className="text-[11px] text-slate-500 block mt-0.5">/fees/defaulters</span>
            </Link>

          </div>
        </div>

        {/* Recent Transactions Table */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="font-bold text-slate-800 text-base">Recent Transactions</h3>
            <Link to="/reports/fees" className="text-xs font-bold text-emerald-600 hover:text-emerald-700">
              View Full Report →
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-wider">
                  <th className="py-3 px-4">Receipt #</th>
                  <th className="py-3 px-4">Student</th>
                  <th className="py-3 px-4">Class</th>
                  <th className="py-3 px-4 text-right">Amount (Rs.)</th>
                  <th className="py-3 px-4 text-right">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {recentTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-6 text-slate-400">
                      No recent transaction records logged.
                    </td>
                  </tr>
                ) : (
                  recentTransactions.map((tx, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 transition">
                      <td className="py-3 px-4 font-mono font-bold text-emerald-700">
                        {tx.receipt_no || `RCP-${tx.id || idx + 1}`}
                      </td>
                      <td className="py-3 px-4 font-bold text-slate-800">
                        {tx.student_name || `Student #${tx.student_id}`}
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {tx.class_name || `Class ${tx.class_id || 10}`}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-emerald-600">
                        +Rs. {Number(tx.amount_paid || tx.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-slate-500">
                        {tx.payment_date ? new Date(tx.payment_date).toLocaleDateString() : new Date().toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};

export default AccountantDashboard;
